import { createContext, useContext, useEffect, useState, type ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api } from './lib/api';
import { takePostLoginPath } from './lib/supabase';
import { useAuth } from './auth/AuthProvider';
import { AppetiteChat } from './components/AppetiteChat';
import { HubConsent } from './components/HubConsent';
import { HubShell } from './components/HubShell';
import { HubNoAccess } from './components/HubNoAccess';
import { SiteNotice } from './components/SiteNotice';
import { TopBar } from './components/TopBar';
import { AgentHome } from './pages/AgentHome';
import { Dashboard } from './pages/Dashboard';
import { Hub } from './pages/Hub';
import { HubAdmin } from './pages/HubAdmin';
import { Supplementals } from './pages/Supplementals';
import { SupplementalFill } from './pages/SupplementalFill';
import { InsuredFinish } from './pages/InsuredFinish';
import { PolicyUpload } from './pages/PolicyUpload';
import { LossRuns } from './pages/LossRuns';
import { ReportLoss } from './pages/ReportLoss';
import { Binders } from './pages/Binders';
import { Requests } from './pages/Requests';
import { Glossary } from './pages/Glossary';
import { Notifications } from './pages/Notifications';
import { Compare } from './pages/Compare';
import { CompareAccount } from './pages/CompareAccount';
import { Inspector } from './pages/Inspector';
import { InspectorAccount } from './pages/InspectorAccount';
import { PolicyUploadLink } from './pages/PolicyUploadLink';
import { BorSign } from './pages/BorSign';
import { Bor } from './pages/Bor';
import { Programs } from './pages/Programs';
import { HubChatTranscript } from './pages/HubChatTranscript';
import { SubmissionPortal } from './pages/SubmissionPortal';
import { SubmissionBuild } from './pages/SubmissionBuild';
import { PortalQueue } from './pages/PortalQueue';
import { Settings } from './pages/Settings';
import { SolutionsTracker } from './pages/SolutionsTracker';
import { StaffDashboard } from './pages/StaffDashboard';
import { SubmissionDetail } from './pages/SubmissionDetail';
import { Wizard } from './pages/Wizard';
import { HUB_OPEN_TO_MEMBERS } from '@commpass/shared';
import type { Role } from './types';

interface RoleState {
  role: Role;
  setRole: (r: Role) => void;
}
const RoleContext = createContext<RoleState>({ role: 'agent', setRole: () => {} });
export const useRole = () => useContext(RoleContext);

/**
 * Routes that get the hub's rail and top bar.
 *
 * ⚠️ COMM-PASS's own routes are deliberately absent. `/`, `/new`, `/s/:id` and
 * `/queue` keep the navy top bar and look exactly as they do in production —
 * an agent who only uses COMM-PASS sees nothing new.
 */
const inShell = (path: string): boolean =>
  path.startsWith('/hub') || path.startsWith('/submission');

export function App() {
  const { isStaff, loading, session } = useAuth();
  const [role, setRole] = useState<Role>('agent');
  const navigate = useNavigate();
  const location = useLocation();

  // When a user becomes staff (after sign-in), default them to the specialist side.
  useEffect(() => {
    setRole(isStaff ? 'aas' : 'agent');
  }, [isStaff]);

  /**
   * Who sees the hub.
   *
   * ⚠️ ENTITLEMENT, NOT A GLOBAL SWITCH (2026-08-30). This used to be
   * `isStaff || HUB_OPEN_TO_MEMBERS` — one flag that shut the hub to every
   * member or opened it to all 243 of them, which made an agency-by-agency
   * rollout impossible. Now a member sees the hub because somebody provisioned
   * them into it, which is also how it gets sold.
   *
   * The constant survives as an override for the day the hub is genuinely
   * general-release; until then it stays false and provisioning decides.
   */
  const [myModules, setMyModules] = useState<string[] | null>(null);
  useEffect(() => {
    /**
     * ⚠️ DO NOT DECIDE ANYTHING WHILE AUTH IS STILL LOADING.
     *
     * Hooks run even when the component returns its loading UI, so this fired
     * on mount with session === null and set modules to [] — "no access". By
     * the time the session finished restoring from storage, the route had
     * already redirected a legitimately provisioned member out of the hub.
     *
     * Empty and unresolved are different states and have to stay different:
     * [] means the server said no, null means nobody has asked yet.
     */
    if (loading) return;
    if (!session) {
      setMyModules([]);
      return;
    }
    setMyModules(null);
    api
      .hubMe()
      .then((r) => setMyModules(r.modules))
      .catch(() => setMyModules([]));
  }, [loading, session]);
  /**
   * ⚠️ NO `isStaff ||` HERE ANY MORE (2026-09-01). That bypass meant every
   * @agentsalliance.biz address saw the Commercial Hub button and could open the
   * Submission Portal, which sends to real underwriters. It surfaced when a
   * colleague built and sent one. Josh: "I'm the only one that should be using
   * the Hub for testing only."
   *
   * The client now believes exactly what the server says. modulesFor() returns
   * everything for an allow-listed staffer and nothing for anyone else, so there
   * is no second opinion here that could disagree with what the API will
   * actually permit — which is how the button stayed visible to people every
   * route behind it would have refused.
   *
   * COMM-PASS is untouched: its staff tools read `isStaff` from the domain, as
   * they always have.
   */
  const hubVisible = HUB_OPEN_TO_MEMBERS || (myModules?.length ?? 0) > 0;

  /**
   * One wrapper for every hub page: the member gate, the consent notice, then
   * the shell. Written once so a new hub route cannot accidentally skip the
   * gate — the mistake that would put an unfinished tool in front of members.
   */
  const hubRoute = (el: ReactElement) => {
    /**
     * ⚠️ WAIT FOR THE ANSWER BEFORE REDIRECTING. myModules is null until
     * /api/hub/me comes back, and treating null as "no access" bounced a
     * legitimately provisioned member to COMM-PASS before the entitlement had
     * even been fetched — the page simply refused to open, with no error and
     * nothing in the console to explain it.
     */
    if (loading || (session && myModules === null)) {
      return (
        <div className="wrap">
          <div className="empty">
            <p>Loading…</p>
          </div>
        </div>
      );
    }
    /**
     * ⚠️ REFUSED OUT LOUD, NOT REDIRECTED. This returned `<Navigate to="/" />`,
     * which bounced an un-entitled member back to COMM-PASS with no error and
     * nothing in the console. Kylie hit it on 2026-09-08 and reported it as
     * "mine is saying it's not found" — a permissions decision was
     * indistinguishable from a broken link, and she was the person evaluating
     * the product.
     *
     * The gate has not moved: `hubVisible` still comes from what the server
     * said and nothing else. Only the answer changed, from a silent redirect to
     * a page saying which of the two things went wrong.
     */
    return hubVisible ? (
      <HubShell>
        <HubConsent>{el}</HubConsent>
      </HubShell>
    ) : (
      <HubNoAccess signedIn={Boolean(session)} />
    );
  };

  // Signing in lands on the hub — it is the front door now. Signing in from a
  // specific page returns you to that page instead. Only a genuine sign-in moves
  // anyone; an existing session refreshing stays exactly where it is.
  //
  // Anonymous agents are untouched by all of this. Someone who only uses
  // COMM-PASS keeps arriving at COMM-PASS, because they never sign in at all.
  useEffect(() => {
    if (loading || !session) return;
    const dest = takePostLoginPath();
    if (dest && dest !== location.pathname) navigate(dest, { replace: true });
    // location is deliberately not a dependency — this runs once per sign-in,
    // not on every navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, session, navigate]);

  // The page title follows the section, so a hub tab isn't labelled COMM-PASS.
  useEffect(() => {
    document.title = location.pathname.startsWith('/hub')
      ? 'Commercial Hub — Agents Alliance'
      : 'COMM-PASS — Submission Portal';
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="wrap">
        <div className="empty">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <RoleContext.Provider value={{ role, setRole }}>
      {/* The insured sees no app chrome. Every link in the top bar goes somewhere
          they have no account for, and a page full of someone else's navigation
          is how a stranger decides a link isn't legitimate. */}
      {/* Hub pages carry the rail instead; COMM-PASS keeps its own bar. */}
      {/*
        ⚠️ ABOVE EVERYTHING, AND NOT ON THE PAGES AN INSURED SEES. A member
        hitting an odd error deserves the context; somebody who was sent a link
        to sign a broker-of-record letter does not need our infrastructure
        problems on the document they are about to put their name to — those
        pages are deliberately bare, and a banner about outages on one reads as
        a reason not to trust it.
      */}
      {!/^\/(finish|sign|policy-upload)\//.test(location.pathname) && <SiteNotice />}
      {!location.pathname.startsWith('/finish/') && !inShell(location.pathname) && <TopBar />}
      {/* Hub-only for now. Dropping a chat launcher onto the live COMM-PASS
          wizard would change what agents see there, and that stays untouched. */}
      {hubVisible && inShell(location.pathname) && <AppetiteChat />}
      <Routes>
        <Route path="/" element={role === 'aas' && isStaff ? <Dashboard /> : <AgentHome />} />
        {/* The hub is additive: existing routes keep working exactly as they did,
            so nothing about the live COMM-PASS experience changes. */}
        {/* Every hub route sits behind the visibility notice. Wrapping each route
            rather than the whole app keeps COMM-PASS's own pages untouched. */}
        {/* Hidden from members until HUB_OPEN_TO_MEMBERS is flipped. Typing the
            URL sends them to COMM-PASS rather than showing a locked door — a
            member has no reason to learn the hub exists before it's theirs. */}
        <Route path="/hub" element={hubRoute(<Hub />)} />
        <Route path="/hub/supplementals" element={hubRoute(<Supplementals />)} />
        {/* Iron Peak only. The page is behind the same gate as the API, and the
            rail entry only appears for someone who holds the module — nobody
            else needs to wonder what it is. */}
        <Route path="/hub/policy-upload" element={hubRoute(<PolicyUpload />)} />
        {/* The standalone Iron Peak link (Josh, 2026-08-31), for members who
            have no hub account yet. Deliberately OUTSIDE the hub shell and
            outside hubRoute: no sign-in, no rail, and no way through to
            anything else. The token in the URL is what makes it work and what
            switches it off — clear POLICY_UPLOAD_LINK_TOKEN and every copy
            anyone has forwarded stops resolving. */}
        <Route path="/policy-upload/:token" element={<PolicyUploadLink />} />
        {/* ⚠️ Public and unauthenticated — the insured signing has no account.
            Outside the hub shell on purpose: this is a document, not a product
            surface, and the person reading it is not a member. */}
        <Route path="/sign/:token" element={<BorSign />} />
        {/* Behind the same `lossruns` grant as the API, so the rail entry and
            the page appear together or not at all. */}
        <Route path="/hub/loss-runs" element={hubRoute(<LossRuns />)} />
        {/* Behind the `fnol` grant, same as the rail entry. */}
        <Route path="/hub/report-a-loss" element={hubRoute(<ReportLoss />)} />
        <Route path="/hub/binders" element={hubRoute(<Binders />)} />
        <Route path="/hub/bor" element={hubRoute(<Bor />)} />
        <Route path="/hub/requests" element={hubRoute(<Requests />)} />
        <Route path="/hub/glossary" element={hubRoute(<Glossary />)} />
        <Route path="/hub/notifications" element={hubRoute(<Notifications />)} />
        <Route path="/hub/compare" element={hubRoute(<Compare />)} />
        <Route path="/hub/compare/:id" element={hubRoute(<CompareAccount />)} />
        {/* The Inspector used to be an absolute URL to another deployment, so
            the rail entry left the hub entirely. It is a route now. The
            standalone deployment keeps running for the agent on it. */}
        <Route path="/hub/inspector" element={hubRoute(<Inspector />)} />
        <Route path="/hub/inspector/:id" element={hubRoute(<InspectorAccount />)} />
        <Route path="/hub/programs" element={hubRoute(<Programs />)} />
        {/* The transcript. The Teams card's "Open the thread" button has
            pointed here since hub chat shipped, with no route behind it. */}
        <Route path="/hub/chat/:id" element={hubRoute(<HubChatTranscript />)} />
        {/* The questionnaire itself — the carrier PDF rendered as a web form. */}
        <Route path="/hub/supplementals/:class" element={hubRoute(<SupplementalFill />)} />
        {/* The insured's route. Deliberately OUTSIDE the hub: no sign-in, no
            consent gate, no top bar full of things they have no account for.
            They are a member of the public who was sent a link, and the page
            should behave like it. */}
        <Route path="/finish/:token" element={<InsuredFinish />} />
        {/* The Submission Portal. Behind the same member gate as the rest of the
            hub, and — like every hub route — it writes only to the hub's own
            tables. COMM-PASS's queue is untouched by anything here. */}
        <Route path="/submission" element={hubRoute(<SubmissionPortal />)} />
        <Route path="/submission/:id" element={hubRoute(<SubmissionBuild />)} />
        {/* AAS's view of what members sent. Staff only, and deliberately NOT
            the COMM-PASS queue — nothing here has been through specialist
            intake. */}
        <Route
          path="/hub/member-submissions"
          element={isStaff ? <HubShell><PortalQueue /></HubShell> : <Navigate to="/" replace />}
        />
        {/* Designed for an agency admin to manage their own team, with RLS
            deciding what they see — but while the hub is closed that means
            staff only, like everything else under /hub. */}
        <Route path="/hub/admin" element={session ? hubRoute(<HubAdmin />) : <Navigate to="/" replace />} />
        <Route path="/queue" element={isStaff ? <StaffDashboard /> : <Navigate to="/" replace />} />
        <Route path="/new" element={<Wizard />} />
        <Route path="/s/:id" element={<SubmissionDetail />} />
        <Route path="/solutions" element={isStaff ? <SolutionsTracker /> : <Navigate to="/" replace />} />
        <Route path="/settings" element={isStaff ? <Settings /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </RoleContext.Provider>
  );
}
